import { Routes } from '@angular/router';
import { authGuard } from './core/auth/auth.guard';
import { roleGuard } from './core/auth/role.guard';
import { unsavedTimingChangesGuard } from './features/tasks/task-edit/unsaved-timing-changes.guard';
import { Roles } from './core/auth/role-constants';
import { LayoutComponent } from './layout/layout.component';

const adminOnly = [Roles.GlobalAdmin, Roles.OrgAdmin, Roles.FacilityAdmin];
const adminAndReception = [...adminOnly, Roles.Reception];

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () =>
      import('./features/login/login/login.component').then((m) => m.LoginComponent),
  },
  {
    path: 'not-authorized',
    loadComponent: () =>
      import('./features/not-authorized/not-authorized/not-authorized.component').then((m) => m.NotAuthorizedComponent),
  },
  {
    path: '',
    component: LayoutComponent,
    canActivate: [authGuard],
    children: [
      { path: '', redirectTo: 'home', pathMatch: 'full' },
      {
        path: 'home',
        loadComponent: () =>
          import('./features/home/home/home.component').then((m) => m.HomeComponent),
      },
      {
        path: 'onboarding',
        loadComponent: () =>
          import('./features/onboarding/onboarding/onboarding.component').then(
            (m) => m.OnboardingComponent
          ),
        canActivate: [roleGuard([Roles.GlobalAdmin, Roles.OrgAdmin])],
      },
      {
        path: 'facility-wizard',
        loadComponent: () =>
          import('./features/facility-wizard/facility-wizard/facility-wizard.component').then(
            (m) => m.FacilityWizardComponent
          ),
        canActivate: [roleGuard([Roles.GlobalAdmin, Roles.OrgAdmin])],
      },
      {
        path: 'organizations',
        loadComponent: () =>
          import('./features/organizations/organization-list/organization-list.component').then(
            (m) => m.OrganizationListComponent
          ),
        canActivate: [roleGuard(adminOnly)],
      },
      {
        path: 'organizations/upsert',
        loadComponent: () =>
          import('./features/organizations/organization-edit/organization-edit.component').then(
            (m) => m.OrganizationEditComponent
          ),
        canActivate: [roleGuard(adminOnly)],
      },
      {
        path: 'organizations/upsert/:id',
        loadComponent: () =>
          import('./features/organizations/organization-edit/organization-edit.component').then(
            (m) => m.OrganizationEditComponent
          ),
        canActivate: [roleGuard(adminOnly)],
      },
      {
        path: 'suites',
        loadComponent: () =>
          import('./features/suites/suite-list/suite-list.component').then(
            (m) => m.SuiteListComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
      },
      {
        path: 'suites/upsert',
        loadComponent: () =>
          import('./features/suites/suite-edit/suite-edit.component').then(
            (m) => m.SuiteEditComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
      },
      {
        path: 'suites/upsert/:id',
        loadComponent: () =>
          import('./features/suites/suite-edit/suite-edit.component').then(
            (m) => m.SuiteEditComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
      },
      {
        path: 'suite-groups',
        loadComponent: () =>
          import('./features/suite-groups/suite-assignment-board/suite-assignment-board.component').then(
            (m) => m.SuiteAssignmentBoardComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
      },
      {
        path: 'employees',
        loadComponent: () =>
          import('./features/employees/employee-list/employee-list.component').then(
            (m) => m.EmployeeListComponent
          ),
        canActivate: [roleGuard(adminOnly)],
      },
      {
        path: 'employees/upsert',
        loadComponent: () =>
          import('./features/employees/employee-edit/employee-edit.component').then(
            (m) => m.EmployeeEditComponent
          ),
        canActivate: [roleGuard(adminOnly)],
      },
      {
        path: 'employees/upsert/:id',
        loadComponent: () =>
          import('./features/employees/employee-edit/employee-edit.component').then(
            (m) => m.EmployeeEditComponent
          ),
        canActivate: [roleGuard(adminOnly)],
      },
      {
        path: 'pet-groups',
        loadComponent: () =>
          import('./features/pet-groups/pet-group-list/pet-group-list.component').then(
            (m) => m.PetGroupListComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
      },
      {
        path: 'pet-groups/upsert',
        loadComponent: () =>
          import('./features/pet-groups/pet-group-edit/pet-group-edit.component').then(
            (m) => m.PetGroupEditComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
      },
      {
        path: 'pet-groups/upsert/:id',
        loadComponent: () =>
          import('./features/pet-groups/pet-group-edit/pet-group-edit.component').then(
            (m) => m.PetGroupEditComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
      },
      {
        path: 'facilities',
        loadComponent: () =>
          import('./features/facilities/facility-list/facility-list.component').then(
            (m) => m.FacilityListComponent
          ),
        canActivate: [roleGuard(adminOnly)],
      },
      {
        path: 'facilities/upsert',
        loadComponent: () =>
          import('./features/facilities/facility-edit/facility-edit.component').then(
            (m) => m.FacilityEditComponent
          ),
        canActivate: [roleGuard(adminOnly)],
      },
      {
        path: 'facilities/upsert/:id',
        loadComponent: () =>
          import('./features/facilities/facility-edit/facility-edit.component').then(
            (m) => m.FacilityEditComponent
          ),
        canActivate: [roleGuard(adminOnly)],
      },
      {
        path: 'tasks',
        loadComponent: () =>
          import('./features/tasks/task-list/task-list.component').then(
            (m) => m.TaskListComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
      },
      {
        path: 'tasks/upsert',
        loadComponent: () =>
          import('./features/tasks/task-edit/task-edit.component').then(
            (m) => m.TaskEditComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
        canDeactivate: [unsavedTimingChangesGuard],
      },
      {
        path: 'tasks/upsert/:id',
        loadComponent: () =>
          import('./features/tasks/task-edit/task-edit.component').then(
            (m) => m.TaskEditComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
        canDeactivate: [unsavedTimingChangesGuard],
      },
      {
        path: 'services',
        loadComponent: () =>
          import('./features/services/service-list/service-list.component').then(
            (m) => m.ServiceListComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
      },
      {
        path: 'services/upsert',
        loadComponent: () =>
          import('./features/services/service-edit/service-edit.component').then(
            (m) => m.ServiceEditComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
      },
      {
        path: 'services/upsert/:id',
        loadComponent: () =>
          import('./features/services/service-edit/service-edit.component').then(
            (m) => m.ServiceEditComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
      },
      {
        path: 'service-add-ons',
        loadComponent: () =>
          import('./features/service-add-ons/service-add-on-list/service-add-on-list.component').then(
            (m) => m.ServiceAddOnListComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
      },
      {
        path: 'service-add-ons/upsert',
        loadComponent: () =>
          import('./features/service-add-ons/service-add-on-edit/service-add-on-edit.component').then(
            (m) => m.ServiceAddOnEditComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
      },
      {
        path: 'service-add-ons/upsert/:id',
        loadComponent: () =>
          import('./features/service-add-ons/service-add-on-edit/service-add-on-edit.component').then(
            (m) => m.ServiceAddOnEditComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
      },
      {
        path: 'timings',
        loadComponent: () =>
          import('./features/timings/timing-list/timing-list.component').then(
            (m) => m.TimingListComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
      },
      {
        path: 'timings/upsert',
        loadComponent: () =>
          import('./features/timings/timing-edit/timing-edit.component').then(
            (m) => m.TimingEditComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
      },
      {
        path: 'timings/upsert/:id',
        loadComponent: () =>
          import('./features/timings/timing-edit/timing-edit.component').then(
            (m) => m.TimingEditComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
      },
      {
        path: 'integrations',
        loadComponent: () =>
          import('./features/integrations/integration-list/integration-list.component').then(
            (m) => m.IntegrationListComponent
          ),
        canActivate: [roleGuard(adminOnly)],
      },
      {
        path: 'integrations/upsert',
        loadComponent: () =>
          import('./features/integrations/integration-edit/integration-edit.component').then(
            (m) => m.IntegrationEditComponent
          ),
        canActivate: [roleGuard(adminOnly)],
      },
      {
        path: 'integrations/upsert/:id',
        loadComponent: () =>
          import('./features/integrations/integration-edit/integration-edit.component').then(
            (m) => m.IntegrationEditComponent
          ),
        canActivate: [roleGuard(adminOnly)],
      },
      {
        path: 'owners',
        loadComponent: () =>
          import('./features/owners/owner-list/owner-list.component').then(
            (m) => m.OwnerListComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
      },
      {
        path: 'owners/upsert',
        loadComponent: () =>
          import('./features/owners/owner-edit/owner-edit.component').then(
            (m) => m.OwnerEditComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
      },
      {
        path: 'owners/upsert/:id',
        loadComponent: () =>
          import('./features/owners/owner-edit/owner-edit.component').then(
            (m) => m.OwnerEditComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
      },
      {
        path: 'reservations',
        loadComponent: () =>
          import('./features/reservations/reservation-list/reservation-list.component').then(
            (m) => m.ReservationListComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
      },
      {
        path: 'reservations/upsert',
        loadComponent: () =>
          import('./features/reservations/reservation-edit/reservation-edit.component').then(
            (m) => m.ReservationEditComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
      },
      {
        path: 'reservations/upsert/:id',
        loadComponent: () =>
          import('./features/reservations/reservation-edit/reservation-edit.component').then(
            (m) => m.ReservationEditComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
      },
      {
        path: 'conditions',
        loadComponent: () =>
          import('./features/facility-tags/facility-tag-list/facility-tag-list.component').then(
            (m) => m.FacilityTagListComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
      },
      {
        path: 'conditions/upsert',
        loadComponent: () =>
          import('./features/facility-tags/facility-tag-edit/facility-tag-edit.component').then(
            (m) => m.FacilityTagEditComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
      },
      {
        path: 'conditions/upsert/:id',
        loadComponent: () =>
          import('./features/facility-tags/facility-tag-edit/facility-tag-edit.component').then(
            (m) => m.FacilityTagEditComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
      },
      {
        path: 'checklists',
        loadComponent: () =>
          import('./features/checklists/checklist-items/checklist-items.component').then(
            (m) => m.ChecklistItemsComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
      },
      {
        path: 'checklists/items/upsert',
        loadComponent: () =>
          import('./features/checklists/checklist-item-edit/checklist-item-edit.component').then(
            (m) => m.ChecklistItemEditComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
      },
      {
        path: 'checklists/items/upsert/:id',
        loadComponent: () =>
          import('./features/checklists/checklist-item-edit/checklist-item-edit.component').then(
            (m) => m.ChecklistItemEditComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
      },
      {
        path: 'checklists/manage',
        loadComponent: () =>
          import('./features/checklists/checklist-list/checklist-list.component').then(
            (m) => m.ChecklistListComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
      },
      {
        path: 'checklists/manage/upsert',
        loadComponent: () =>
          import('./features/checklists/checklist-edit/checklist-edit.component').then(
            (m) => m.ChecklistEditComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
      },
      {
        path: 'checklists/manage/upsert/:id',
        loadComponent: () =>
          import('./features/checklists/checklist-edit/checklist-edit.component').then(
            (m) => m.ChecklistEditComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
      },
      {
        path: 'forms',
        loadComponent: () =>
          import('./features/forms/form-list/form-list.component').then(
            (m) => m.FormListComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
      },
      {
        path: 'forms/upsert',
        loadComponent: () =>
          import('./features/forms/form-edit/form-edit.component').then(
            (m) => m.FormEditComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
      },
      {
        path: 'forms/upsert/:id',
        loadComponent: () =>
          import('./features/forms/form-edit/form-edit.component').then(
            (m) => m.FormEditComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
      },
      {
        path: 'broadcast-banners',
        loadComponent: () =>
          import('./features/broadcast-banners/broadcast-banner-list/broadcast-banner-list.component').then(
            (m) => m.BroadcastBannerListComponent
          ),
        canActivate: [roleGuard([Roles.GlobalAdmin])],
      },
      {
        path: 'broadcast-banners/upsert',
        loadComponent: () =>
          import('./features/broadcast-banners/broadcast-banner-edit/broadcast-banner-edit.component').then(
            (m) => m.BroadcastBannerEditComponent
          ),
        canActivate: [roleGuard([Roles.GlobalAdmin])],
      },
      {
        path: 'broadcast-banners/upsert/:id',
        loadComponent: () =>
          import('./features/broadcast-banners/broadcast-banner-edit/broadcast-banner-edit.component').then(
            (m) => m.BroadcastBannerEditComponent
          ),
        canActivate: [roleGuard([Roles.GlobalAdmin])],
      },
      {
        path: 'feedback',
        loadComponent: () =>
          import('./features/feedback/feedback/feedback.component').then(
            (m) => m.FeedbackComponent
          ),
        canActivate: [roleGuard([Roles.GlobalAdmin])],
      },
      {
        path: 'reports',
        loadComponent: () =>
          import('./features/reports/reports/reports.component').then(
            (m) => m.ReportsComponent
          ),
        canActivate: [roleGuard(adminOnly)],
      },
      {
        path: 'user-management',
        loadComponent: () =>
          import('./features/user-management/user-management/user-management.component').then(
            (m) => m.UserManagementComponent
          ),
        canActivate: [roleGuard(adminOnly)],
      },
      {
        path: 'pets',
        loadComponent: () =>
          import('./features/pets/pet-list/pet-list.component').then(
            (m) => m.PetListComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
      },
      {
        path: 'pets/upsert',
        loadComponent: () =>
          import('./features/pets/pet-edit/pet-edit.component').then(
            (m) => m.PetEditComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
      },
      {
        path: 'pets/upsert/:id',
        loadComponent: () =>
          import('./features/pets/pet-edit/pet-edit.component').then(
            (m) => m.PetEditComponent
          ),
        canActivate: [roleGuard(adminAndReception)],
      },
    ],
  },
  { path: '**', redirectTo: 'home' },
];
